import { PresenterView } from '@/components/PresenterView'
import { BottomBar } from './BottomBar'
import { SidebarContainer } from '@/components/sidebar/SidebarContainer'
import MemorizedParticipantView from './ParticipantView'
import useIsMobile from '@/hooks/useIsMobile'
import useIsTab from '@/hooks/useIsTab'
import { useMediaQuery } from 'react-responsive'
import { useEffect, useRef, useState } from 'react'
import { useParticipant } from '@videosdk.live/react-sdk'
import { useSocketContext } from '@/contexts/SocketProvider'
import { useHall } from '@/contexts/HallProvider'
import { useMeetingAppContext } from '@/MeetingAppContextDef'
import Joyride, { ACTIONS, STATUS, CallBackProps } from 'react-joyride'
import { sideBarModes } from '@/utils/common'
import { FlowNotesModal } from '@/components/debateTools/flowNotes/FlowNotes'
import { GuidebookModal } from '@/components/debateTools/guidebook/Guidebook'
import { PollingModal } from '@/components/debateTools/polling/Poll'
import { SyncedEvidenceModal } from '@/components/debateTools/evidence/Evidence'
import { ownerSteps, viewerSteps, TurnType } from '@db8/shared'
import { BallotModal } from '@/components/debateTools/ballot/BallotModal'
import { WinnerAnnouncement } from '@/components/WinnerAnnouncement'
import { useAxios } from '@/contexts/AxiosProvider'
import { useHallUsers } from '@/contexts/HallUsersProvider'
import { useTurns } from '@/contexts/TurnsProvider'

type MeetingRoomProps = {
  mMeeting: any
  containerHeight: number
  setIsMeetingLeft: (v: boolean) => void
}

const MeetingRoom: React.FC<MeetingRoomProps> = ({
  mMeeting,
  containerHeight,
  setIsMeetingLeft,
}) => {
  const [participantsData, setParticipantsData] = useState<string[]>([])
  const { hall, isOwner, runTour, setRunTour } = useHall()
  const { currentUser } = useHallUsers()
  const socket = useSocketContext()
  const { secondSideBarMode, setSideBarMode, allowAutoAudio } = useMeetingAppContext()
  const { currentTurn } = useTurns()

  const [stepIndex, setStepIndex] = useState(0)
  const axios = useAxios()

  const isMobile = useIsMobile()
  const isTab = useIsTab()
  const isLGDesktop = useMediaQuery({ minWidth: 1024, maxWidth: 1439 })
  const isXLDesktop = useMediaQuery({ minWidth: 1440 })
  const sidebarRef = useRef<HTMLDivElement | null>(null)

  const isPresenting = !!mMeeting?.presenterId
  const ignorePins = currentTurn?.type === TurnType.Preperatory

  const sideBarContainerWidth = isXLDesktop
    ? 350
    : isLGDesktop
      ? 350
      : isTab
        ? 350
        : isMobile
          ? 280
          : 240
  const bottomBarHeight = 60

  useEffect(() => {
    const debounceTimeout = setTimeout(() => {
      const participantIds = Array.from(mMeeting.participants.keys()) as string[]
      setParticipantsData(participantIds)
    }, 500)
    return () => clearTimeout(debounceTimeout)
  }, [mMeeting.participants])

  const ParticipantMicStream: React.FC<{ participantId: string }> = ({ participantId }) => {
    const { micStream } = useParticipant(participantId)
    useEffect(() => {
      if (!allowAutoAudio) return
      if (micStream?.track) {
        const mediaStream = new MediaStream()
        mediaStream.addTrack(micStream.track)
        const audioElement = new Audio()
        audioElement.srcObject = mediaStream
        void audioElement.play().catch(() => {})
      }
    }, [micStream, participantId, allowAutoAudio])
    return null
  }

  useEffect(() => {
    if (hall?.id) socket.emit('join-hall', { hallId: hall.id })
    return () => {
      if (hall?.id) socket.emit('leave-hall', { hallId: hall.id })
    }
  }, [hall?.id, socket])

  const restartTour = () => {
    setRunTour(false)
    setStepIndex(0)
  }

  const handleJoyrideCallback = async (data: CallBackProps) => {
    const { action, index, status, type, lifecycle } = data
    if (isOwner) {
      if (index === 4) setSideBarMode(sideBarModes.SEQUENCE)
      if (index === 5) setSideBarMode(sideBarModes.CHAT)
      if (index === 6) setSideBarMode(sideBarModes.PARTICIPANTS)
      if (index === 7) setSideBarMode(sideBarModes.SETTINGS)
      if (index === 7 && lifecycle === 'complete') setSideBarMode(null)
    }
    if (['step:after', 'error:target_not_found'].includes(type)) {
      setStepIndex(index + (action === ACTIONS.PREV ? -1 : 1))
    } else if (status === STATUS.FINISHED || status === STATUS.SKIPPED) restartTour()
    if (action === 'close') {
      try {
        if (currentUser?.clerkId)
          await axios.put(`/users/${currentUser.clerkId}`, { hasSeenTour: true })
      } catch {}
      setSideBarMode(sideBarModes.PARTICIPANTS)
      restartTour()
    }
  }

  const stageHeight = containerHeight

  return (
    <>
      <Joyride
        // @ts-ignore
        steps={isOwner ? ownerSteps : viewerSteps}
        continuous
        callback={handleJoyrideCallback}
        stepIndex={stepIndex}
        styles={{
          options: {
            backgroundColor: '#f7f9fb',
            overlayColor: 'rgba(1, 8, 22, 0.6)',
            textColor: 'black',
            zIndex: 9999,
          },
        }}
        disableBeacon
        showProgress
        run={runTour}
      />

      {hall && (
        <>
          <FlowNotesModal />
          <GuidebookModal />
          <WinnerAnnouncement />
          <PollingModal />
          <BallotModal />
          <SyncedEvidenceModal />
        </>
      )}

      {/* MAIN SCROLL AREA (only scroller on page) */}
      <div
        className="flex-1 min-h-0 overflow-auto"
        style={{ paddingBottom: `calc(${bottomBarHeight}px + env(safe-area-inset-bottom))` }}
      >
        <div className="flex h-full w-full min-h-0">
          {/* LEFT: stage / participants */}
          <main className="flex-1 min-w-0 min-h-0">
            <div className="flex h-full min-h-0">
              {isPresenting ? <PresenterView height={stageHeight} /> : null}

              {isPresenting && isMobile ? (
                participantsData.map((participantId) => (
                  <ParticipantMicStream key={participantId} participantId={participantId} />
                ))
              ) : (
                <MemorizedParticipantView
                  sidebarRef={sidebarRef}
                  isPresenting={isPresenting}
                  ignorePins={ignorePins}
                />
              )}
            </div>
          </main>

          {/* MIDDLE: second sidebar (only if open) */}
          {secondSideBarMode && (
            <SidebarContainer
              height={stageHeight}
              sideBarContainerWidth={sideBarContainerWidth}
              mode="secondSideBar"
            />
          )}

          {/* RIGHT: primary sidebar */}
          <SidebarContainer height={stageHeight} sideBarContainerWidth={sideBarContainerWidth} />
        </div>
      </div>

      {/* FIXED BOTTOM BAR */}
      <div
        className="fixed left-0 right-0 z-30 bg-background"
        style={{
          bottom: 'calc(0px + var(--safe-bottom))',
          height: bottomBarHeight,
          boxShadow: '0 -4px 12px rgba(0,0,0,0.2)',
        }}
      >
        <BottomBar bottomBarHeight={bottomBarHeight} setIsMeetingLeft={setIsMeetingLeft} />
      </div>
    </>
  )
}

export default MeetingRoom
